import { useCallback, useEffect } from 'react';

import Cookies from 'js-cookie';
import { useCookie } from 'react-use';
import { create } from 'zustand';

import { ANONYMOUS_ID_COOKIE_NAME, DEVICE_ID_COOKIE_NAME, RAPTOR_REAID_COOKIE_NAME } from '~/features/tracking/constants';
import { useCookieConsent } from '~/shared/components/CookieConsent/hooks/useCookieConsent';
import { isSSR } from '~/shared/utils/platform';

/**
 * Not really UUID, use with caution.
 * In theory, it's 1 in 10^48 chances of collision, in practice Math.random is not that good but good enough
 */
const getPseudoUUID = () => `${Math.random().toString(36).substring(2)}-${Math.random().toString(36).substring(2)}-${Math.random().toString(36).substring(2)}`;

function uuid() {
	return crypto.randomUUID ? crypto.randomUUID() : getPseudoUUID();
}

type UseRaptorEnvironmentReturnData = {
	/** Updates the reaId parameter used for Raptor tracking. */
	setReaId(id: string): void;
	/** Updates the ruid parameter used for Raptor tracking. */
	setRuid(id: string): void;
	/** Maps to "coid" parameter in Raptor request */
	cookieId?: string;
	/** Maps to "sid" parameter in Raptor request */
	sessionId?: string;
	/** Maps to "reaid" parameter in Raptor request */
	reaId?: string;
	/** Maps to "ruid" parameter in Raptor request */
	ruid?: string;
};

type SessionStore = {
	cookieId?: string;
	sessionId?: string;

	setCookieId: (id: string) => void;
	setSessionId: (id: string) => void;

	clearCookieId: () => void;
	clearSessionId: () => void;
};

// Cookies and useCookie() aren't really stateful, which is bad when we rely on them across the site. So we'll
// use Zustand to handle the actual values, thank you very much.
const useCookieSessionStore = create<SessionStore>((set) => ({
	// Initialize from cookies
	cookieId: Cookies.get(DEVICE_ID_COOKIE_NAME),
	sessionId: Cookies.get(ANONYMOUS_ID_COOKIE_NAME),

	setCookieId: (id) => {
		Cookies.set(DEVICE_ID_COOKIE_NAME, id, {
			expires: 365 * 5,
			sameSite: 'lax',
		});

		set({ cookieId: id });
	},

	setSessionId: (id) => {
		// Session cookie → no expires
		Cookies.set(ANONYMOUS_ID_COOKIE_NAME, id, {
			sameSite: 'lax',
		});

		set({ sessionId: id });
	},

	clearCookieId: () => {
		Cookies.remove(DEVICE_ID_COOKIE_NAME);
		set({ cookieId: undefined });
	},

	clearSessionId: () => {
		Cookies.remove(ANONYMOUS_ID_COOKIE_NAME);
		set({ sessionId: undefined });
	},
}));

export const useRaptorEnvironment = (): UseRaptorEnvironmentReturnData => {
	const { allowStatistics } = useCookieConsent();
	const [reaId, setReaId] = useCookie(RAPTOR_REAID_COOKIE_NAME);
	const [ruid, setRuid] = useCookie('rsaRuid');
	const { cookieId, setCookieId, setSessionId, sessionId } = useCookieSessionStore();

	useEffect(() => {
		if (isSSR) {
			return;
		}

		if (!sessionId) {
			// We set the anonymous id cookie for each session.
			// Options parameter with expiresAt property omitted to create a session cookie.
			setSessionId(uuid());
		}

		if (!allowStatistics || cookieId) {
			return;
		}

		setCookieId(uuid());
	}, [allowStatistics, cookieId, sessionId, setCookieId, setSessionId]);

	const updateReaId = useCallback(
		(id: string) => {
			if (!id) return;
			setReaId(id, { expires: 365 });
		},
		[setReaId],
	);

	const updateRuid = useCallback(
		(id: string) => {
			if (!id) return;
			setRuid(id, { expires: 365 });
		},
		[setRuid],
	);

	if (!allowStatistics) return { setReaId: updateReaId, setRuid: updateRuid, cookieId: undefined };

	return {
		setReaId: updateReaId,
		setRuid: updateRuid,
		cookieId: cookieId || undefined,
		sessionId: sessionId || undefined,
		reaId: reaId || undefined,
		ruid: ruid || undefined,
	};
};
